/**
 * Serviço de WebSocket para notificações em tempo real
 * Inclui reconexão automática com exponential backoff
 */

import { getApiBaseUrl, getWsBaseUrl } from '../utils/apiUrl';

const API_URL = getApiBaseUrl();

class WebSocketNotificationsService {
  constructor() {
    this.ws = null;
    this.userId = null;
    this.reconnectAttempts = 0;
    this.maxReconnectAttempts = 10;
    this.baseReconnectDelay = 1000;
    this.maxReconnectDelay = 30000;
    this.pingInterval = null;
    this.isManualClose = false;
    this.onNotificationCallback = null;
    this.onConnectedCallback = null;
    this.onDisconnectedCallback = null;
    this.onInitialNotificationsCallback = null;
  }

  /**
   * Conecta ao WebSocket de notificações
   * @param {string} userId - ID do usuário
   */
  connect(userId) {
    if (!userId) {
      console.error('[WS Notifications] userId is required');
      return;
    }

    this.userId = userId;
    this.isManualClose = false;
    this.reconnectAttempts = 0;

    this._createConnection();
  }

  _createConnection() {
    // Fecha conexão existente se houver
    if (this.ws) {
      try {
        this.ws.close();
      } catch (e) {}
      this.ws = null;
    }

    const wsBase = getWsBaseUrl();
    const fullUrl = `${wsBase}/ws/notifications/${this.userId}`;

    console.log('[WS Notifications] Connecting to:', fullUrl);

    try {
      this.ws = new WebSocket(fullUrl);
      
      // Timeout para conexão - não bloquear UI indefinidamente
      const connectionTimeout = setTimeout(() => {
        if (this.ws && this.ws.readyState === WebSocket.CONNECTING) {
          console.log('[WS Notifications] Connection timeout, closing...');
          this.ws.close();
        }
      }, 10000);

      this.ws.onopen = () => {
        clearTimeout(connectionTimeout);
        console.log('[WS Notifications] Connected!');
        this.reconnectAttempts = 0;
        
        if (this.onConnectedCallback) {
          this.onConnectedCallback(true);
        }

        // Inicia ping interval
        this._startPingInterval();
      };

      this.ws.onmessage = (event) => {
        try {
          const data = JSON.parse(event.data);
          this._handleMessage(data);
        } catch (error) {
          console.error('[WS Notifications] Error parsing message:', error);
        }
      };

      this.ws.onclose = (event) => {
        clearTimeout(connectionTimeout);
        console.log('[WS Notifications] Disconnected:', event.code, event.reason);
        
        this._stopPingInterval();
        
        if (this.onDisconnectedCallback) {
          this.onDisconnectedCallback();
        }

        if (this.onConnectedCallback) {
          this.onConnectedCallback(false);
        }

        // Tenta reconectar se não foi fechamento manual (com limite)
        if (!this.isManualClose && this.reconnectAttempts < this.maxReconnectAttempts) {
          this._scheduleReconnect();
        } else if (this.reconnectAttempts >= this.maxReconnectAttempts) {
          console.log('[WS Notifications] Max reconnect attempts reached, stopping...');
        }
      };

      this.ws.onerror = (error) => {
        clearTimeout(connectionTimeout);
        console.error('[WS Notifications] Error:', error);
        // Não fazer nada aqui - onclose será chamado
      };

    } catch (error) {
      console.error('[WS Notifications] Connection error:', error);
      // Agendar reconexão em background, não bloquear
      if (this.reconnectAttempts < this.maxReconnectAttempts) {
        this._scheduleReconnect();
      }
    }
  }

  _handleMessage(data) {
    switch (data.type) {
      case 'notification':
        console.log('[WS Notifications] New notification:', data.title);
        if (this.onNotificationCallback) {
          this.onNotificationCallback(data);
        }
        break;

      case 'initial_notifications':
        console.log('[WS Notifications] Initial notifications:', data.unread_count);
        if (this.onInitialNotificationsCallback) {
          this.onInitialNotificationsCallback(data);
        }
        break;

      case 'pong':
        // Resposta do ping - conexão está viva
        break;

      case 'all_marked_read':
        console.log('[WS Notifications] All notifications marked as read');
        break;

      default:
        console.log('[WS Notifications] Unknown message type:', data.type);
    }
  }

  _startPingInterval() {
    this._stopPingInterval();
    this.pingInterval = setInterval(() => {
      if (this.ws && this.ws.readyState === WebSocket.OPEN) {
        this.ws.send(JSON.stringify({ type: 'ping' }));
      }
    }, 30000); // Ping a cada 30 segundos
  }

  _stopPingInterval() {
    if (this.pingInterval) {
      clearInterval(this.pingInterval);
      this.pingInterval = null;
    }
  }

  _scheduleReconnect() {
    this.reconnectAttempts++;
    
    // Exponential backoff
    const delay = Math.min(
      this.baseReconnectDelay * Math.pow(2, this.reconnectAttempts - 1),
      this.maxReconnectDelay
    );

    console.log(`[WS Notifications] Reconnecting in ${delay}ms (attempt ${this.reconnectAttempts}/${this.maxReconnectAttempts})`);

    setTimeout(() => {
      if (!this.isManualClose) {
        this._createConnection();
      }
    }, delay);
  }

  /**
   * Desconecta do WebSocket
   */
  disconnect() {
    console.log('[WS Notifications] Disconnecting...');
    this.isManualClose = true;
    this._stopPingInterval();
    
    if (this.ws) {
      this.ws.close();
      this.ws = null;
    }
    
    this.userId = null;
  }

  /**
   * Envia comando para marcar notificação como lida
   * @param {string} notificationId - ID da notificação
   */
  markAsRead(notificationId) {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({
        type: 'mark_read',
        notification_id: notificationId
      }));
    }
  }

  /**
   * Envia comando para marcar todas como lidas
   */
  markAllAsRead() {
    if (this.ws && this.ws.readyState === WebSocket.OPEN) {
      this.ws.send(JSON.stringify({ type: 'mark_all_read' }));
    }
  }

  /**
   * Define callback para quando receber notificação
   * @param {Function} callback 
   */
  onNotification(callback) {
    this.onNotificationCallback = callback;
  }

  /**
   * Define callback para mudança de estado de conexão
   * @param {Function} callback 
   */
  onConnectionChange(callback) {
    this.onConnectedCallback = callback;
  }

  /**
   * Define callback para quando desconectar
   * @param {Function} callback 
   */
  onDisconnected(callback) {
    this.onDisconnectedCallback = callback;
  }

  /**
   * Define callback para notificações iniciais
   * @param {Function} callback 
   */
  onInitialNotifications(callback) {
    this.onInitialNotificationsCallback = callback;
  }

  /**
   * Verifica se está conectado
   * @returns {boolean}
   */
  isConnected() {
    return this.ws && this.ws.readyState === WebSocket.OPEN;
  }

  /**
   * Retorna estado atual da conexão
   * @returns {string}
   */
  getState() {
    if (!this.ws) return 'DISCONNECTED';
    switch (this.ws.readyState) {
      case WebSocket.CONNECTING: return 'CONNECTING';
      case WebSocket.OPEN: return 'CONNECTED';
      case WebSocket.CLOSING: return 'CLOSING';
      case WebSocket.CLOSED: return 'DISCONNECTED';
      default: return 'UNKNOWN';
    }
  }
}

// Singleton instance
const wsNotificationsService = new WebSocketNotificationsService();

export default wsNotificationsService;
